"use client";

import { CURRENCY } from "@/config";
import { Star } from "lucide-react";
import Image from "next/image";
import type { CSFeaturedDish } from "@/types";

interface FeaturedDishProps {
  data: CSFeaturedDish;
}

export default function FeaturedDish({ data }: FeaturedDishProps) {
  const { imageSrc, price, rating, reviews, badgeLabel } = data;
  const stars = Math.round(rating);
  const starIds = ["s1", "s2", "s3", "s4", "s5"];

  return (
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center mb-20">
        <div className="lg:col-span-2">
          <div className="relative overflow-hidden rounded-3xl">
            <div className="relative w-full h-96">
              <Image
                src={imageSrc}
                alt={data.title}
                fill
                className="object-cover transition-transform duration-1000 hover:scale-105"
                unoptimized
                sizes="(max-width: 1024px) 100vw, 66vw"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute top-8 left-8">
              <span className="bg-yellow-500 text-black px-4 py-2 rounded-full text-sm font-bold">
                {badgeLabel}
              </span>
            </div>
            <div className="absolute bottom-8 left-8 right-8">
              <div className="flex items-end justify-between">
                <div>
                  <h3 className="text-4xl font-bold text-white mb-2">01.</h3>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex items-center space-x-1">
                      {starIds.map((id, i) => (
                        <Star
                          key={id}
                          className="w-4 h-4 text-yellow-400"
                          aria-hidden="true"
                          fill={i < stars ? "currentColor" : "none"}
                        />
                      ))}
                    </div>
                    <span className="text-white/80 text-sm">
                      ({rating.toFixed(1)} • {reviews} reviews)
                    </span>
                  </div>
                </div>
                <span className="text-5xl font-bold text-white">
                  {CURRENCY}
                  {price}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <div>
            <h3 className="text-4xl font-bold text-black mb-4">{data.title}</h3>
            <p className="text-black/70 leading-relaxed mb-6">
              Our signature creation featuring the finest San Marzano tomatoes,
              fresh mozzarella di bufala, premium black truffle shavings, and
              aromatic fresh basil. Crafted on our signature sourdough base and
              finished with extra virgin olive oil.
            </p>
            <div className="space-y-4">
              {(data.details || []).map((d) => (
                <div
                  key={`${d.icon}-${d.text}`}
                  className="flex items-center gap-3"
                >
                  {/* Use Lucide icons by name via a simple map */}
                  <span className="w-5 h-5 text-black/50 inline-block">
                    {d.icon === "clock" && (
                      <svg
                        viewBox="0 0 24 24"
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    )}
                    {d.icon === "flame" && (
                      <svg
                        viewBox="0 0 24 24"
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M8.5 14.5A4.5 4.5 0 1 0 13 9s1 2-1 4-3.5 1.5-3.5 1.5Z"></path>
                      </svg>
                    )}
                    {d.icon === "award" && (
                      <svg
                        viewBox="0 0 24 24"
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="8" r="6"></circle>
                        <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"></path>
                      </svg>
                    )}
                  </span>
                  <span className="text-black/70">{d.text}</span>
                </div>
              ))}
            </div>
          </div>
          {/* shopping-cart icon */}
          {/* <button
            type="button"
            className="w-full bg-black text-white py-4 rounded-full font-medium hover:bg-black/80 transition-colors flex items-center justify-center gap-3"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
            Add to Order
          </button> */}
        </div>
      </div>
    </div>
  );
}
